import { useMutation } from "@tanstack/react-query";
import { CircleX, MessageSquareWarning } from "lucide-react";
import { type ComponentProps, type ReactNode, useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import type { IssueDetail } from "../../types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";

type ButtonProps = Pick<ComponentProps<typeof Button>, "size" | "variant" | "className" | "disabled">;

/** Approves the merge after a confirmation. */
export function SignOffButton({
  issue,
  onSigned,
  children,
  ...button
}: ButtonProps & { issue: IssueDetail; onSigned: () => void; children?: ReactNode }) {
  const approve = useMutation({
    mutationFn: () => api.signOffIssue(issue.identifier),
    onSuccess: () => {
      toast.success("Signed off", { description: "It merges once its checks pass." });
      onSigned();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Sign-off failed"),
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button {...button} disabled={button.disabled || approve.isPending}>
          {approve.isPending && <Spinner />}
          {children ?? "Sign off and merge"}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sign off {issue.identifier}?</AlertDialogTitle>
          <AlertDialogDescription>
            Skynet approves the pull request. It merges once its checks pass, the issue moves to Done, and the
            change ships to production with the next release.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Not yet</AlertDialogCancel>
          <AlertDialogAction onClick={() => approve.mutate()}>Sign off and merge</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Cancels the issue after a confirmation: the pull request closes unmerged and the preview stops. */
export function CancelIssueButton({
  issue,
  onCancelled,
  children,
  ...button
}: ButtonProps & { issue: IssueDetail; onCancelled: () => void; children?: ReactNode }) {
  const cancel = useMutation({
    mutationFn: () => api.cancelIssue(issue.identifier),
    onSuccess: () => {
      toast.success(`${issue.identifier} cancelled`, {
        description: "The pull request was closed without merging.",
      });
      onCancelled();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Cancel failed"),
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="destructive" {...button} disabled={button.disabled || cancel.isPending}>
          {children ?? <>{cancel.isPending ? <Spinner /> : <CircleX className="size-4" />} Cancel</>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel {issue.identifier}?</AlertDialogTitle>
          <AlertDialogDescription>
            Skynet closes the pull request for “{issue.title}” without merging it, moves the issue to
            Cancelled, and stops the preview. The change will not ship.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep testing</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={() => cancel.mutate()}>
            Cancel issue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Sends feedback to the Fixer. `defaultFeedback` prefills the form each time it opens. */
export function RequestChangesDialog({
  issue,
  onSent,
  defaultFeedback = "",
  children,
  ...button
}: ButtonProps & {
  issue: IssueDetail;
  onSent: () => void;
  defaultFeedback?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const send = useMutation({
    mutationFn: () => api.requestIssueChanges(issue.identifier, feedback.trim()),
    onSuccess: () => {
      setFeedback("");
      setOpen(false);
      toast.success("Sent to the Fixer", { description: "It comes back to business testing once fixed." });
      onSent();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Request failed"),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (send.isPending) return;
        if (next) setFeedback(defaultFeedback);
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" {...button}>
          {children ?? (
            <>
              <MessageSquareWarning className="size-4" /> Request changes
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <form
          className="grid gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (feedback.trim() && !send.isPending) send.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Request changes</DialogTitle>
            <DialogDescription>
              Skynet posts your feedback on the issue and sends it to the Fixer. The fix is reviewed again,
              then comes back to business testing for a new sign-off.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="sign-off-feedback">What needs to change</FieldLabel>
            <Textarea
              id="sign-off-feedback"
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              placeholder="For example: the discount shows on the cart but the checkout total is still full price."
              rows={defaultFeedback ? 8 : 4}
              autoFocus
            />
            <FieldDescription>Say what you did, what you expected, and what happened.</FieldDescription>
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={send.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!feedback.trim() || send.isPending}>
              {send.isPending && <Spinner />}
              Send to the Fixer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
